import { create } from "zustand";

/** The current status of a background job. */
export type JobStatus = "pending" | "running" | "completed" | "failed";

/** A background job that runs in a web worker. */
export type Job<T = unknown> = {
  /** Unique identifier for the job. */
  id: string;
  /** The job type in format "modulePath:functionName". */
  type: string;
  /** Current execution status. */
  status: JobStatus;
  /** The result returned by the worker function. */
  result?: T;
  /** Error message if the job failed. */
  error?: string;
  /** Latest activity reported by the algorithm. */
  progressMessage?: string;
  lastActivityAt?: number;
  /** Timestamp when the job was created. */
  createdAt: number;
  /** Timestamp when the job completed or failed. */
  completedAt?: number;
};

type JobStore = {
  jobs: Job[];
  add: (id: string, type: string) => void;
  setRunning: (id: string) => void;
  setProgress: (id: string, message: string) => void;
  setCompleted: <T>(id: string, result: T) => void;
  setFailed: (id: string, error: string) => void;
  remove: (id: string) => void;
  clear: () => void;
};

export const useJobStore = create<JobStore>((set) => ({
  jobs: [],

  add(id, type) {
    set((state) => ({
      jobs: [
        ...state.jobs,
        { id, type, status: "pending", createdAt: Date.now() },
      ],
    }));
  },

  setRunning(id) {
    set((state) => ({
      jobs: state.jobs.map((job) =>
        job.id === id ? { ...job, status: "running" } : job,
      ),
    }));
  },

  setCompleted(id, result) {
    set((state) => ({
      jobs: state.jobs.map((job) =>
        job.id === id
          ? { ...job, status: "completed", result, completedAt: Date.now() }
          : job,
      ),
    }));
  },

  setProgress(id, message) {
    set((state) => ({
      jobs: state.jobs.map((job) =>
        job.id === id && job.status === "running"
          ? { ...job, progressMessage: message, lastActivityAt: Date.now() }
          : job,
      ),
    }));
  },

  setFailed(id, error) {
    set((state) => ({
      jobs: state.jobs.map((job) =>
        job.id === id
          ? { ...job, status: "failed", error, completedAt: Date.now() }
          : job,
      ),
    }));
  },

  remove(id) {
    set((state) => ({
      jobs: state.jobs.filter((job) => job.id !== id),
    }));
  },

  clear() {
    set({ jobs: [] });
  },
}));
